import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import {
  type GroupMember,
  type GroupSnapshot,
  groupMessageMetadata,
  groupUIMessages,
} from '@crowdex/groups';
import type { PluginConversationContext } from '@crowdex/host-kit/host';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@crowdex/ui/ai/conversation';
import { Button } from '@crowdex/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { decodeAgentMentionLabel } from '@crowdex/ui/lib/inline-reference';
import { UserRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DesktopMessageList } from '@/components/desktop-message-list';
import { PluginConversationComposerContext } from '@/features/plugins/conversation-composer-context';
import { groupCommand } from '@/features/voice-call/group-room';
import { InterruptedActionContext } from '@/lib/conversations/message-part/interrupted-action-context';

/** How often an open group's room is read while its members may answer. */
const POLL_MS = 1_500;
const AUTO = '__auto';

/** The `@` mentions of the composer as members, and the words without their markup. */
function mentions(
  text: string,
  members: readonly GroupMember[],
): { text: string; recipientIds: string[] } {
  const recipientIds: string[] = [];
  const plain = text.replace(
    /@\[((?:\\.|[^\]\\])+)\]\(agent:([^)]+)\)/g,
    (_, label: string, id: string) => {
      if (members.some((member) => member.id === id)) recipientIds.push(id);
      return `@${decodeAgentMentionLabel(label)}`;
    },
  );
  return { text: plain, recipientIds };
}

/**
 * A group conversation: the user and several of their assistants, each answering from its own
 * conversation (`@crowdex/groups`, hosted by the server). Its transcript shows who said what and
 * to whom; who answers now, and why one could not, shows above the composer, whose `@` (or the
 * recipient picker) chooses who a message is for.
 */
export function GroupConversation({
  conversationId,
  initialMessages,
  onMessagesChange,
  composer,
}: PluginConversationContext) {
  const [room, setRoom] = useState<GroupSnapshot | null>(null);
  const [recipient, setRecipient] = useState(AUTO);
  const [replyTo, setReplyTo] = useState<string | undefined>();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revision = useRef(-1);
  const initial = useRef(initialMessages);
  const changed = useRef(onMessagesChange);
  changed.current = onMessagesChange;

  const accept = useCallback((value: GroupSnapshot) => {
    if (value.revision <= revision.current) return;
    revision.current = value.revision;
    setRoom(value);
    if (value.messages.length > 0) changed.current?.(groupUIMessages(value, initial.current));
  }, []);

  useEffect(() => {
    revision.current = -1;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        accept(await groupCommand(conversationId, { type: 'status' }, controller.signal));
        setError(null);
      } catch (reason) {
        if (!controller.signal.aborted)
          setError(reason instanceof Error ? reason.message : String(reason));
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(poll, POLL_MS);
      }
    };
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [conversationId, accept]);

  const members: GroupMember[] = useMemo(
    () =>
      room?.participants ??
      initialMessages.map(groupMessageMetadata).find((metadata) => metadata !== undefined)
        ?.participants ??
      [],
    [room, initialMessages],
  );
  const messages: CrowdexUIMessage[] = useMemo(
    () => (room === null ? [...initialMessages] : groupUIMessages(room, initialMessages)),
    [room, initialMessages],
  );
  const nameOf = (id: string) => members.find((member) => member.id === id)?.name ?? id;
  const answering = room?.activeParticipantIds.map(nameOf) ?? [];

  const act = async (command: Parameters<typeof groupCommand>[1]) => {
    accept(await groupCommand(conversationId, command));
  };

  return (
    <div className="@container relative flex min-h-0 flex-1 overflow-hidden">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Conversation className="min-h-0 flex-1">
          <ConversationContent className="mx-auto w-full max-w-conversation gap-4 px-5 py-5">
            {messages.length === 0 && (
              <p className="text-muted-foreground text-sm">
                Talk with {members.map((member) => member.name).join(', ') || 'your assistants'}:
                name one, or ask the group.
              </p>
            )}
            {messages.map((message) => {
              const metadata = groupMessageMetadata(message);
              const mine = message.role === 'user';
              return (
                <div
                  className={`flex items-start gap-3 ${mine ? 'flex-row-reverse' : ''}`}
                  key={message.id}
                >
                  <span
                    aria-hidden="true"
                    className={`mt-1 flex size-8 shrink-0 items-center justify-center rounded-full font-semibold text-xs ${mine ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}
                  >
                    {mine ? 'YO' : <UserRound className="size-4" />}
                  </span>
                  <div
                    className={`min-w-0 max-w-[90%] flex-1 rounded-2xl border border-hairline bg-muted/30 px-4 py-3 ${mine ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
                  >
                    <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                      <span className="font-medium">
                        {metadata?.authorName ?? (mine ? 'You' : 'Assistant')}
                      </span>
                      {(metadata?.recipientIds ?? []).map((id) => (
                        <span key={id}>→ {nameOf(id)}</span>
                      ))}
                      <Button
                        className="ml-auto"
                        onClick={() => {
                          setReplyTo(message.id);
                          setRecipient(
                            !mine && members.some((member) => member.id === metadata?.authorId)
                              ? (metadata?.authorId ?? AUTO)
                              : AUTO,
                          );
                        }}
                        size="sm"
                        type="button"
                        variant="ghost"
                      >
                        Reply
                      </Button>
                    </div>
                    <InterruptedActionContext.Provider value={null}>
                      <DesktopMessageList embedded messages={[message]} />
                    </InterruptedActionContext.Provider>
                  </div>
                </div>
              );
            })}
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>
        <div className="mx-auto w-full max-w-conversation px-5 text-muted-foreground text-xs">
          {answering.length > 0 && (
            <p role="status">
              {answering.join(', ')} {answering.length > 1 ? 'are' : 'is'} answering…
            </p>
          )}
          {Object.entries(room?.errors ?? {}).map(([id, reason]) => (
            <p className="text-destructive" key={id} role="alert">
              {nameOf(id)}: {reason}
            </p>
          ))}
          {room?.paused && (
            <p className="flex items-center gap-2">
              {room.pauseReason}
              <Button
                onClick={() => void act({ type: 'resume' })}
                size="sm"
                type="button"
                variant="ghost"
              >
                Resume
              </Button>
            </p>
          )}
          {error && (
            <p className="text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <PluginConversationComposerContext.Provider
          value={{
            participants: members.map((member) => ({
              id: member.id,
              name: member.name,
              agentId: '',
            })),
            allowEveryone: false,
            fixed: true,
            placeholder: 'Message the group, or use @name…',
            recipientControl: (
              <Select onValueChange={setRecipient} value={recipient}>
                <SelectTrigger
                  aria-label="Message recipient"
                  className="h-control-compact w-auto max-w-36 border-0 bg-transparent text-xs shadow-none"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>Auto</SelectItem>
                  {members.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      @{member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ),
            replyControl: replyTo && (
              <div className="flex items-center gap-1 px-3 pt-2 text-muted-foreground text-xs">
                Replying to:{' '}
                {groupMessageMetadata(messages.find((message) => message.id === replyTo) ?? {})
                  ?.authorName ?? 'You'}
                <Button
                  onClick={() => setReplyTo(undefined)}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Cancel reply
                </Button>
              </div>
            ),
            ready: room !== null && members.length >= 2,
            pending: sending,
            send: async (message) => {
              if (message.files.length > 0)
                throw new Error('A group takes text only: remove the attachments.');
              const { text, recipientIds } = mentions(message.text, members);
              if (!text.trim()) throw new Error('Enter a message.');
              setSending(true);
              try {
                await act({
                  type: 'send',
                  id: crypto.randomUUID(),
                  text,
                  recipientIds:
                    recipientIds.length > 0 ? recipientIds : recipient === AUTO ? [] : [recipient],
                  ...(replyTo === undefined ? {} : { replyTo }),
                });
                setReplyTo(undefined);
              } finally {
                setSending(false);
              }
            },
          }}
        >
          {composer}
        </PluginConversationComposerContext.Provider>
      </div>
    </div>
  );
}
